import React, { useState, useMemo, useEffect } from 'react';
import {
  PlusIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  UserIcon,
  CalendarIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  ShareIcon,
  ExclamationTriangleIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useQuotations } from '../../hooks/useQuotations';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { format } from 'date-fns';
import SearchBar from '../common/SearchBar';
import LoadingScreen from '../common/LoadingScreen';
import { Quotation, CreateQuotationData } from '../../types/quotation';
import { Tooltip } from '../common/Tooltip';
import QuotationPreview from './QuotationPreview';
import QuotationForm from './QuotationForm';
import toast from 'react-hot-toast';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import QuotationPdfContent from './QuotationPdfContent';
import { useLanguage } from '../../contexts/LanguageContext';
import HungarianQuotationPdfContent from './HungarianQuotationPdfContent';
import { generateHungarianPdf } from './generateHungarianPdf';
import Pagination from '../common/Pagination';
import Modal from '../common/Modal';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import DocumentGeneratorModal from 'components/documents/DocumentGeneratorModal';
import DocumentViewerModal from 'components/documents/DocumentViewerModal';
import QuotationViewModal from './QuotationViewModal';
import ShareQuotationModal from './ShareQuotationModal';
import { 
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface QuotationListProps {
  onNewClick: () => void;
  hideHeader?: boolean;
}

const statusConfig = {
  draft: {
    icon: PencilIcon,
    label: 'Draft',
    color: 'text-gray-400',
    hoverColor: 'hover:text-gray-300',
    bgColor: 'bg-gray-500/10'
  },
  editing: {
    icon: PencilIcon,
    label: 'Editing',
    color: 'text-blue-400',
    hoverColor: 'hover:text-blue-300',
    bgColor: 'bg-blue-500/10'
  },
  pending: {
    icon: ClockIcon,
    label: 'Pending',
    color: 'text-yellow-400',
    hoverColor: 'hover:text-yellow-300',
    bgColor: 'bg-yellow-500/10'
  },
  rejected: {
    icon: XCircleIcon,
    label: 'Rejected',
    color: 'text-red-400',
    hoverColor: 'hover:text-red-300',
    bgColor: 'bg-red-500/10'
  },
  success: {
    icon: CheckCircleIcon,
    label: 'Success',
    color: 'text-green-400',
    hoverColor: 'hover:text-green-300',
    bgColor: 'bg-green-500/10'
  }
} as const;

const statusFlow = {
  draft: 'editing',
  editing: 'pending',
  pending: 'success',
  success: 'rejected',
  rejected: 'draft'
} as const;

const QuotationList: React.FC<QuotationListProps> = ({ onNewClick, hideHeader = false }) => {
  const {
    quotations,
    loading: quotationsLoading,
    deleteQuotation,
    duplicateQuotation,
    updateQuotation,
    refreshQuotations
  } = useQuotations();
  const { organizations, loading: orgsLoading } = useOrganizations();
  const { customers, loading: customersLoading } = useCustomers();
  const { products, loading: productsLoading } = useProducts();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isDocGenOpen, setIsDocGenOpen] = useState(false);
  const { t, language } = useLanguage();
  const [selectedQuotations, setSelectedQuotations] = useState<string[]>([]);
  const [quotationsToDelete, setQuotationsToDelete] = useState<Quotation[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [selectedQuotationForView, setSelectedQuotationForView] = useState<Quotation | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // ShareQuotationModal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [quotationToShare, setQuotationToShare] = useState<Quotation | null>(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Reset selections when filters change
  useEffect(() => {
    setSelectedQuotations([]);
  }, [searchTerm, selectedOrgs, selectedStatuses]);

  const getCustomerName = (customerId: string) => {
    const customer = customers?.find(c => c.id === customerId);
    if (!customer) return 'Unknown Customer';

    return customer.type === 'organization'
      ? customer.companyName
      : `${customer.firstName} ${customer.lastName}`;
  };

  const filteredQuotations = useMemo(() => {
    return quotations?.filter(quotation => {
      const customerName = getCustomerName(quotation.customerId) || '';
      const searchMatch = 
        (quotation.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
        (quotation.number.toLowerCase().includes(searchTerm.toLowerCase())) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase());

      const orgMatch = selectedOrgs.length === 0 || selectedOrgs.includes(quotation.organizationId);
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(quotation.status);

      return searchMatch && orgMatch && statusMatch;
    }) || [];
  }, [quotations, searchTerm, selectedOrgs, selectedStatuses, customers]);

  // Pagination logic
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuotations = filteredQuotations.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleExportPDF = async (quotation: Quotation) => {
    try {
      const org = organizations.find(org => org.id === quotation.organizationId);
      const cust = customers.find(c => c.id === quotation.customerId);
      const quotationProducts = products.filter(p =>
        quotation.items.some(item => item.productId === p.id)
      );

      const pdf = generateHungarianPdf(
        new jsPDF(),
        quotation,
        org,
        cust,
        quotationProducts
      );

      pdf.save(`${quotation.quotationNumber || quotation.number}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('quotations.messages.downloadError'));
    }
  };

  const handleDuplicate = async (quotation: Quotation) => {
    try {
      await duplicateQuotation(quotation.id);
    } catch (error) {
      console.error('Error duplicating quotation:', error);
    }
  };

  const handleStatusChange = async (quotation: Quotation) => {
    try {
      const newStatus = statusFlow[quotation.status];

      await updateQuotation(quotation.id, {
        ...quotation,
        status: newStatus
      });

      toast.success(`${t('common.messages.quotation.offerStatusUpdated')} ${t(`quotations.status.${newStatus}`)}`);
    } catch (error) {
      console.error('Error updating quotation status:', error);
      toast.error(t('common.messages.quotation.offerStatusUpdateFailed'));
    }
  };

  const calculateTotal = (quotation: Quotation) => {
    return quotation.items.reduce((sum, item) => {
      const itemTotal = item.totalWithTax || (item.price * item.quantity * (1 + item.taxRate / 100));
      return sum + itemTotal;
    }, 0);
  };

  const handleShareQuotation = (quotation: Quotation) => {
    setQuotationToShare(quotation);
    setIsShareModalOpen(true);
  };

  const handleOrgFilter = (orgId: string) => {
    setSelectedOrgs(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handlePreview = async (quotation: Quotation) => {
    try {
      // Teljes részletes adatok lekérése a Firestore-ból, beleértve a dokumentumokat is
      const docRef = doc(db, 'quotations', quotation.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const quotationData = docSnap.data();
        
        // Segédfüggvény a Timestamp vagy string dátumok kezelésére
        const convertFirestoreDate = (dateField: any, fieldName?: string) => {
          // A paymentDue mező már nem dátum, hanem napok száma
          if (fieldName === 'paymentDue') {
            return dateField;
          }
          
          if (!dateField) return undefined;
          
          // Ha Timestamp objektum, akkor konvertáljuk Date-té
          if (dateField && typeof dateField === 'object' && 'toDate' in dateField) {
            return dateField.toDate();
          }
          
          // Ha string, akkor hagyjuk úgy
          return dateField;
        };

        // Az adatokat kiegészítjük az id-val, ami a dokumentum azonosítója
        // és minden dátum mezőt megfelelően konvertálunk
        const fullQuotationData = {
          ...quotationData,
          id: quotation.id,
          createdAt: quotationData.createdAt?.toDate() || new Date(),
          updatedAt: quotationData.updatedAt?.toDate() || new Date(),
          // Speciális dátum mezők kezelése
          validUntil: convertFirestoreDate(quotationData.validUntil, 'validUntil'),
          date: convertFirestoreDate(quotationData.date, 'date'),
          paymentDue: convertFirestoreDate(quotationData.paymentDue, 'paymentDue'),
          contractStart: convertFirestoreDate(quotationData.contractStart, 'contractStart'),
          contractEnd: convertFirestoreDate(quotationData.contractEnd, 'contractEnd'),
          // Cég adatok biztosítása
          companyName: quotationData.companyName || '',
          companyAddress: quotationData.companyAddress || '',
          taxNumber: quotationData.taxNumber || '',
          registrationNumber: quotationData.registrationNumber || '',
          contactName: quotationData.contactName || '',
          // Mérési adatok biztosítása
          consumption: quotationData.consumption || '',
          price: quotationData.price || '',
          priceUnit: quotationData.priceUnit || '',
          // POD adatok biztosítása
          pod: quotationData.pod || {
            id: '',
            address: '',
            measurementType: '',
            annualConsumption: '',
            power: '',
            profileNumber: ''
          },
          // További POD adatok
          additionalPods: quotationData.additionalPods || [],
          // Egyéb adatok
          notes: quotationData.notes || '',
          kat: quotationData.kat || { kat: '', text: '' },
          // Termékek
          items: quotationData.items || [],
          // Dokumentumok
          documents: quotationData.documents || []
        } as Quotation;
        
        // Most már a teljes adatokkal állítjuk be a kiválasztott ajánlatot dokumentumokkal együtt
        console.log("Dokumentumok megjelenítése:", fullQuotationData.documents);
        setSelectedQuotationForView(fullQuotationData);
        setIsDocumentViewerOpen(true);
      } else {
        console.error('Az ajánlat nem található!');
        toast.error(t('common.messages.quotation.notFound'));
      }
    } catch (error) {
      console.error('Hiba az ajánlat dokumentumainak betöltése közben:', error);
      toast.error(t('common.messages.quotation.documentsLoadError'));
    }
  };

  const handleView = async (quotation: Quotation) => {
    try {
      // Teljes részletes adatok lekérése a Firestore-ból
      const docRef = doc(db, 'quotations', quotation.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const quotationData = docSnap.data();
        
        // Segédfüggvény a Timestamp vagy string dátumok kezelésére
        const convertFirestoreDate = (dateField: any, fieldName?: string) => {
          // A paymentDue mező már nem dátum, hanem napok száma
          if (fieldName === 'paymentDue') {
            return dateField;
          }
          
          if (!dateField) return undefined;
          
          // Ha Timestamp objektum, akkor konvertáljuk Date-té
          if (dateField && typeof dateField === 'object' && 'toDate' in dateField) {
            return dateField.toDate();
          }
          
          // Ha string, akkor hagyjuk úgy
          return dateField;
        };

        // Az adatokat kiegészítjük az id-val, ami a dokumentum azonosítója
        // és minden dátum mezőt megfelelően konvertálunk
        const fullQuotationData = {
          ...quotationData,
          id: quotation.id,
          createdAt: quotationData.createdAt?.toDate() || new Date(),
          updatedAt: quotationData.updatedAt?.toDate() || new Date(),
          // Speciális dátum mezők kezelése
          validUntil: convertFirestoreDate(quotationData.validUntil, 'validUntil'),
          date: convertFirestoreDate(quotationData.date, 'date'),
          paymentDue: convertFirestoreDate(quotationData.paymentDue, 'paymentDue'),
          contractStart: convertFirestoreDate(quotationData.contractStart, 'contractStart'),
          contractEnd: convertFirestoreDate(quotationData.contractEnd, 'contractEnd'),
          // Cég adatok biztosítása
          companyName: quotationData.companyName || '',
          companyAddress: quotationData.companyAddress || '',
          taxNumber: quotationData.taxNumber || '',
          registrationNumber: quotationData.registrationNumber || '',
          contactName: quotationData.contactName || '',
          // Mérési adatok biztosítása
          consumption: quotationData.consumption || '',
          price: quotationData.price || '',
          priceUnit: quotationData.priceUnit || '',
          // POD adatok biztosítása
          pod: quotationData.pod || {
            id: '',
            address: '',
            measurementType: '',
            annualConsumption: '',
            power: '',
            profileNumber: ''
          },
          // További POD adatok
          additionalPods: quotationData.additionalPods || [],
          // Egyéb adatok
          notes: quotationData.notes || '',
          kat: quotationData.kat || { kat: '', text: '' },
          // Termékek
          items: quotationData.items || [],
          // Dokumentumok
          documents: quotationData.documents || []
        } as Quotation;
        
        // Most már a teljes adatokkal állítjuk be a kiválasztott ajánlatot
        console.log("Teljes ajánlat adatok:", fullQuotationData);
        setSelectedQuotation(fullQuotationData);
        setIsViewModalOpen(true);
      } else {
        console.error('Az ajánlat nem található!');
        toast.error(t('common.messages.quotation.notFound'));
      }
    } catch (error) {
      console.error('Hiba az ajánlat betöltése közben:', error);
      toast.error(t('common.messages.quotation.loadError'));
    }
  };

  // Biztonságos dátum formázó függvény
  const formatSafeDate = (dateString: any) => {
    if (!dateString) return '-';
    try {
      // Ellenőrizzük, hogy Timestamp objektum-e
      if (dateString && typeof dateString === 'object' && 'toDate' in dateString) {
        try {
          const date = dateString.toDate();
          if (isNaN(date.getTime())) return '-';
          return format(date, 'yyyy-MM-dd');
        } catch (e) {
          return '-';
        }
      }
      
      // Ha string, ellenőrizzük, hogy a dátum string helyes formátumú-e
      if (typeof dateString === 'string') {
        if (!dateString.match(/^\d{4}-\d{2}-\d{2}/) && !dateString.match(/^\d{4}\/\d{2}\/\d{2}/)) {
          return '-';
        }
      }
      
      const date = new Date(dateString);
      // Ellenőrizzük, hogy érvényes dátum-e
      if (isNaN(date.getTime())) {
        return '-';
      }
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Invalid date format:', dateString, error);
      return '-';
    }
  };

  const handleBulkDelete = () => {
    const quotationsToDelete = filteredQuotations.filter(q => selectedQuotations.includes(q.id));
    setQuotationsToDelete(quotationsToDelete);
    setSelectedQuotation(null);
    setIsConfirmDeleteOpen(true);
  };

  const handleDelete = async (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setQuotationsToDelete([quotation]);
    setIsConfirmDeleteOpen(true);
  };

  // Selected quotations logic
  const allVisible = filteredQuotations.length > 0;
  const allSelected = allVisible && filteredQuotations.every(q => selectedQuotations.includes(q.id));
  const someSelected = selectedQuotations.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedQuotations([]);
    } else {
      setSelectedQuotations(filteredQuotations.map(q => q.id));
    }
  };

  const handleSelectQuotation = (id: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedQuotations(prev => [...prev, id]);
    } else {
      setSelectedQuotations(prev => prev.filter(qId => qId !== id));
    }
  };

  if (quotationsLoading || orgsLoading || customersLoading || productsLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4">
      {!hideHeader && (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">
            {t('quotations.title')}
          </h2>
          <button
            onClick={onNewClick}
            className="bg-primary-600 hover:bg-primary-700 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm"
          >
            <PlusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
            {t('quotations.new')}
          </button>
        </div>
      )}

      <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {selectedQuotations.length > 0 && (
          <div className="bg-primary-50 border-b border-primary-100 p-2 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-primary-700 text-sm font-medium mr-2">
                {selectedQuotations.length} {t('quotations.selected')}
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-md text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="h-3.5 w-3.5 mr-1" />
              {t('quotations.bulkDelete')}
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between p-3 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row w-full items-start sm:items-center justify-between gap-2">
            {/* Szűrők mobil nézetben egy gombra kattintással jelennek meg */}
            <div className="flex sm:hidden w-full">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder={t('common.placeholders.selectQuotation')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <button
                className="ml-2 bg-gray-200 p-1.5 rounded-md text-gray-600"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Mobil nézetben lenyíló szűrők */}
            {isFilterOpen && (
              <div className="sm:hidden w-full mt-2 flex flex-wrap gap-2">
                {/* Status filters in mobile view */}
                <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 w-full">
                  <button
                    onClick={() => setSelectedStatuses([])}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                      ${selectedStatuses.length === 0
                        ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <DocumentIcon className="w-3.5 h-3.5 mr-1" />
                    {t('quotations.filter.allStatuses')}
                  </button>
                  {Object.entries(statusConfig).map(([status, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusFilter(status)}
                        className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                          ${selectedStatuses.includes(status)
                            ? status === 'success' 
                              ? 'border-green-500 bg-green-500/20 text-green-600'
                              : status === 'editing' 
                                ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                                : status === 'pending' 
                                  ? 'border-amber-500 bg-amber-500/20 text-amber-600'
                                  : status === 'rejected' 
                                    ? 'border-red-500 bg-red-500/20 text-red-600'
                                    : 'border-gray-500 bg-gray-500/20 text-gray-600'
                            : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                          }`}
                      >
                        <Icon className="w-3.5 h-3.5 mr-1" />
                        {t(`quotations.status.${status}`)}
                      </button>
                    );
                  })}
                </div>

                {/* Organization filters in mobile view */}
                <div className="flex flex-wrap gap-1 sm:gap-2 w-full">
                  <button
                    onClick={() => setSelectedOrgs([])}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                      ${selectedOrgs.length === 0
                        ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <BuildingOffice2Icon className="w-3.5 h-3.5 mr-1" />
                    {t('quotations.filter.allOrgs')}
                  </button>
                  {organizations?.map(org => (
                    <button
                      key={org.id}
                      onClick={() => handleOrgFilter(org.id)}
                      className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                        ${selectedOrgs.includes(org.id)
                          ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                          : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                    >
                      <BuildingOffice2Icon className="w-3.5 h-3.5 mr-1 text-blue-600" />
                      <span className="max-w-[120px] truncate">{org.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Desktop search bar - moved to be before filters */}
            <div className="hidden sm:block max-w-xs">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('common.placeholders.selectQuotation')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
            </div>

            {/* Desktop status filter buttons */}
            <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none">
              <button
                onClick={() => setSelectedStatuses([])}
                className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                  ${selectedStatuses.length === 0
                    ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
              >
                <DocumentIcon className="w-3.5 h-3.5 mr-1" />
                {t('quotations.fields.status')}: {t('quotations.filter.allStatuses')}
              </button>
              {Object.entries(statusConfig).map(([status, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(status)}
                    className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                      ${selectedStatuses.includes(status)
                        ? status === 'success' 
                          ? 'border-green-500 bg-green-500/20 text-green-600'
                          : status === 'editing' 
                            ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                            : status === 'pending' 
                              ? 'border-amber-500 bg-amber-500/20 text-amber-600'
                              : status === 'rejected' 
                                ? 'border-red-500 bg-red-500/20 text-red-600'
                                : 'border-gray-500 bg-gray-500/20 text-gray-600'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 mr-1" />
                    {t(`quotations.status.${status}`)}
                  </button>
                );
              })}
            </div>

            <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none ml-auto">
              <button
                onClick={() => setSelectedOrgs([])}
                className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                  ${selectedOrgs.length === 0
                    ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
              >
                <BuildingOffice2Icon className="w-3.5 h-3.5 mr-1" />
                {t('customers.fields.organizations')}: {t('quotations.filter.allOrgs')}
              </button>
              {organizations?.map(org => (
                <button
                  key={org.id}
                  onClick={() => handleOrgFilter(org.id)}
                  className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                    ${selectedOrgs.includes(org.id)
                      ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                >
                  <BuildingOffice2Icon className="w-3.5 h-3.5 mr-1 text-blue-600" />
                  <span className="max-w-[120px] truncate">{org.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-2 py-2 w-8">
                  <div className="flex items-center justify-center">
                    <div 
                      className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer
                        ${allSelected 
                          ? 'bg-primary-500 border-primary-500 text-white' 
                          : someSelected 
                            ? 'bg-primary-100 border-primary-300' 
                            : 'border-gray-300'
                        }`}
                      onClick={handleSelectAll}
                    >
                      {allSelected && <CheckIcon className="w-3 h-3" />}
                      {someSelected && <div className="w-2 h-2 bg-primary-500 rounded-sm"></div>}
                    </div>
                  </div>
                </th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  {t('quotations.fields.number')}
                </th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('quotations.fields.organization')}
                </th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('quotations.fields.customer')}
                </th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('quotations.fields.status')}
                </th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {t('quotations.fields.validUntil')}
                </th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('quotations.fields.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuotations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-2 text-center text-gray-500">
                    {t('quotations.noOffer')}
                  </td>
                </tr>
              ) : (
                currentQuotations.map(quotation => {
                  const Icon = statusConfig[quotation.status].icon;
                  const organization = organizations.find(org => org.id === quotation.organizationId);
                  const customer = customers.find(c => c.id === quotation.customerId);
                  const borderColor = quotation.status === 'success'
                    ? 'bg-green-400'
                    : quotation.status === 'editing'
                      ? 'bg-blue-400'
                      : quotation.status === 'pending'
                        ? 'bg-yellow-400'
                        : quotation.status === 'rejected'
                          ? 'bg-red-400'
                          : '';

                  return (
                    <tr
                      key={quotation.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center">
                          <div 
                            className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer
                              ${selectedQuotations.includes(quotation.id) 
                                ? 'bg-primary-500 border-primary-500 text-white' 
                                : 'border-gray-300 hover:border-gray-400'
                              }`}
                            onClick={() => handleSelectQuotation(quotation.id, !selectedQuotations.includes(quotation.id))}
                          >
                            {selectedQuotations.includes(quotation.id) && <CheckIcon className="w-3 h-3" />}
                          </div>
                        </div>
                      </td>
                      <td className="pl-3 pr-3 py-2 relative">
                        <div className={`absolute left-0 top-0 bottom-0 w-2 ${borderColor}`}></div>
                        <div className="flex items-center ml-1">
                          <div className="p-1.5 rounded bg-gradient-to-br from-purple-100 to-purple-200 mr-3 flex-shrink-0">
                            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
                          </div>
                          <div className="min-w-0 w-full">
                            <div className="text-sm font-medium text-gray-800 whitespace-normal break-words overflow-visible">
                              {quotation.quotationNumber || quotation.number}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(quotation.createdAt).toLocaleDateString('hu-HU')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center">
                          {organization && (
                            <div className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20 max-w-[150px] overflow-hidden">
                              <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                              <span className="truncate">{organization.name}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-800">
                          {getCustomerName(quotation.customerId)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-center">
                          <Tooltip content={`${t('common.messages.quotation.updateStatus')}: ${t(`quotations.status.${statusFlow[quotation.status]}`)}`}>
                            <button
                              onClick={() => handleStatusChange(quotation)}
                              className={`transition-colors duration-200 flex items-center gap-2 px-2 py-1 rounded-full text-xs
                                ${statusConfig[quotation.status].color}
                                ${statusConfig[quotation.status].hoverColor}
                                ${statusConfig[quotation.status].bgColor}`}
                            >
                              <Icon className="h-4 w-4" />
                              <span className="text-xs font-medium">
                                {t(`quotations.status.${quotation.status}`)}
                              </span>
                            </button>
                          </Tooltip>
                        </div>
                      </td>

                      <td className="px-3 py-2 text-left">
                        <div className="text-sm text-gray-800">
                          {formatSafeDate(quotation.validUntil)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-center space-x-2">
                          <Tooltip content={t('quotations.tooltips.viewDocuments')}>
                            <button
                              onClick={() => handlePreview(quotation)}
                              className="text-gray-500 hover:text-primary-600"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.tooltips.viewDetails')}>
                            <button
                              onClick={() => handleView(quotation)}
                              className="text-gray-500 hover:text-primary-600"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.tooltips.duplicate')}>
                            <button
                              onClick={() => handleDuplicate(quotation)}
                              className="text-gray-500 hover:text-blue-600"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.tooltips.edit')}>
                            <button
                              onClick={() => {
                                setSelectedQuotation(quotation);
                                setIsEditOpen(true);
                              }}
                              className="text-gray-500 hover:text-primary-600"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.tooltips.share')}>
                            <button
                              onClick={() => handleShareQuotation(quotation)}
                              className="text-gray-500 hover:text-blue-600"
                            >
                              <ShareIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.tooltips.delete')}>
                            <button
                              onClick={() => handleDelete(quotation)}
                              className="text-gray-500 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination integrated within the table container for desktop */}
        {filteredQuotations.length > 0 && (
          <div className="border-t border-gray-200 p-3 bg-gray-50">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredQuotations.length}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </div>

      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden space-y-3">
        {currentQuotations.map(quotation => {
          const Icon = statusConfig[quotation.status].icon;
          const organization = organizations.find(org => org.id === quotation.organizationId);
          
          return (
            <div 
              key={quotation.id} 
              className="bg-white rounded-lg border-l-4 border-gray-200 shadow-sm w-full overflow-hidden"
              style={{ 
                borderLeftColor: quotation.status === 'success'
                  ? '#4ade80'
                  : quotation.status === 'editing'
                    ? '#60a5fa'
                    : quotation.status === 'pending'
                      ? '#fbbf24'
                      : quotation.status === 'rejected'
                        ? '#f87171'
                        : '#9ca3af'
              }}
            >
              <div className="p-2 sm:p-3">
                <div className="flex flex-col w-full">
                  <div className="flex items-center mb-1.5 sm:mb-2 w-full">
                    <div 
                      className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer mr-2 flex-shrink-0
                        ${selectedQuotations.includes(quotation.id) 
                          ? 'bg-primary-500 border-primary-500 text-white' 
                          : 'border-gray-300'
                        }`}
                      onClick={() => handleSelectQuotation(quotation.id, !selectedQuotations.includes(quotation.id))}
                    >
                      {selectedQuotations.includes(quotation.id) && <CheckIcon className="w-3 h-3" />}
                    </div>
                    <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-purple-100 to-purple-200 mr-1 sm:mr-2 flex-shrink-0">
                      <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                    </div>
                    <div className="min-w-0 w-full overflow-visible">
                      <div className="text-xs sm:text-sm font-medium text-gray-800 whitespace-normal break-words">
                        {quotation.quotationNumber || quotation.number}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        {new Date(quotation.createdAt).toLocaleDateString('hu-HU')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex flex-wrap gap-1 mt-1">
                      {organization && (
                        <div className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20 max-w-[150px] overflow-hidden">
                          <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                          <span className="truncate">{organization.name}</span>
                        </div>
                      )}
                      <div className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 max-w-[150px] overflow-hidden">
                        <UserIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                        <span className="truncate">{getCustomerName(quotation.customerId)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center flex-shrink-0 ml-auto space-x-0 sm:space-x-1">
                    <button
                      onClick={() => handleStatusChange(quotation)}
                      className={`transition-colors duration-200 px-2 py-1 rounded-full text-xs
                        ${statusConfig[quotation.status].color}
                        ${statusConfig[quotation.status].hoverColor}
                        ${statusConfig[quotation.status].bgColor} flex items-center`}
                    >
                      <Icon className="h-3 w-3 mr-1" />
                      <span className="text-[10px]">{t(`quotations.status.${quotation.status}`)}</span>
                    </button>
                    <button
                      onClick={() => handlePreview(quotation)}
                      className="p-1 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-500/10"
                    >
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => handleDuplicate(quotation)}
                      className="p-1 rounded-full text-gray-500 hover:text-blue-600 hover:bg-blue-500/10"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedQuotation(quotation);
                        setIsEditOpen(true);
                      }}
                      className="p-1 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-500/10"
                    >
                      <PencilSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => handleShareQuotation(quotation)}
                      className="p-1 rounded-full text-gray-500 hover:text-blue-600 hover:bg-blue-500/10"
                    >
                      <ShareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>
                  </div>
                </div>

                <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
                  <div className="flex items-center text-gray-500">
                    <span className="font-medium text-gray-700 mr-1">{t('quotations.fields.validUntil')}:</span>
                    <span>{formatSafeDate(quotation.validUntil)}</span>
                  </div>
                </div>

                <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
                  <div className="flex items-center text-gray-500">
                    <span className="font-medium text-gray-700 mr-1">{t('quotations.fields.createdAt')}:</span>
                    <span>{new Date(quotation.createdAt).toLocaleDateString('hu-HU')}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {selectedQuotations.length > 0 && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-10 bg-primary-50 border border-primary-100 shadow-lg rounded-lg px-4 py-2 flex items-center gap-3">
            <span className="text-primary-700 text-sm font-medium">
              {selectedQuotations.length} {t('quotations.selected')}
            </span>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-md text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="h-3.5 w-3.5 mr-1" />
              {t('quotations.bulkDelete')}
            </button>
          </div>
        )}
        
        {/* Pagination for mobile view */}
        {filteredQuotations.length > 0 && (
          <div className="relative z-10 mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredQuotations.length}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {selectedQuotation && (
        <QuotationPreview
          quotation={selectedQuotation}
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedQuotation(null);
          }}
        />
      )}

      {/* Edit Modal */}
      {selectedQuotation && (
        <QuotationForm
          isOpen={isEditOpen}
          initialData={selectedQuotation}
          onSubmit={async (data: CreateQuotationData) => {
            try {
              await updateQuotation(selectedQuotation.id, data);
              setIsEditOpen(false);
              setSelectedQuotation(null);
            } catch (error) {
              console.error('Error updating quotation:', error);
            }
          }}
          onCancel={() => {
            setIsEditOpen(false);
            setSelectedQuotation(null);
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setSelectedQuotation(null);
          setQuotationsToDelete([]);
        }}
        onConfirm={() => {
          if (quotationsToDelete.length > 0) {
            Promise.all(quotationsToDelete.map(quotation => deleteQuotation(quotation.id)))
              .then(() => {
                const message = quotationsToDelete.length > 1 
                  ? t('quotations.messages.bulkDeleted', { count: quotationsToDelete.length })
                  : t('quotations.messages.deleted');
                toast.success(message);
                setSelectedQuotations([]);
              })
              .catch(error => {
                console.error('Error deleting quotations:', error);
                toast.error(t('quotations.messages.deleteError'));
              })
              .finally(() => {
                setIsConfirmDeleteOpen(false);
                setSelectedQuotation(null);
                setQuotationsToDelete([]);
              });
          }
        }}
        title={quotationsToDelete.length > 1 ? t('quotations.bulkDeleteTitle') : t('quotations.messages.confirmTitle')}
        deleteMessage={
          quotationsToDelete.length > 1 
            ? t('quotations.bulkDeleteMessage', { count: quotationsToDelete.length }) 
            : `${t('quotations.messages.deleteQuestion')} "${selectedQuotation?.quotationNumber || selectedQuotation?.number || ''}"?`
        }
        deleteWarning={quotationsToDelete.length > 1 ? t('quotations.bulkDeleteWarning') : t('quotations.messages.deleteWarning')}
      />

      {/* Dokumentum generátor Modal */}
      {selectedQuotation && (
        <DocumentGeneratorModal
          isOpen={isDocGenOpen}
          onClose={() => {
            setIsDocGenOpen(false);
            setSelectedQuotation(null);
            // Refresh the quotations list to update the documents
            refreshQuotations();
          }}
          documentDataProvider={selectedQuotation}
        />
      )}

      {/* Dokumentum megjelenítő modal */}
      {selectedQuotationForView && (
        <DocumentViewerModal
          isOpen={isDocumentViewerOpen}
          onClose={() => {
            setIsDocumentViewerOpen(false);
            setSelectedQuotationForView(null);
          }}
          customerWithDocuments={selectedQuotationForView}
        />
      )}

      {/* Árajánlat részletek modal */}
      {selectedQuotation && (
        <QuotationViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedQuotation(null);
          }}
          quotation={selectedQuotation}
          onEdit={(quotation) => {
            setIsViewModalOpen(false);
            setSelectedQuotation(quotation);
            setIsEditOpen(true);
          }}
        />
      )}

      {/* Share Quotation Modal */}
      <ShareQuotationModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        quotation={quotationToShare}
      />
    </div>
  );
};

export default QuotationList; 